import { useState, useEffect, useContext, useRef } from 'react'
import { useNavigate, useParams } from "react-router-dom"
import moment from 'moment-timezone'
import { isValidDateTimeInput } from '/utils/date-time'

import _ from "lodash"

import { enqueueSnackbar } from 'notistack'
import { Backdrop, CircularProgress } from '@mui/material'

import Body from './body'

import { AppContext } from "/providers/app-context"

import loadTemplate from '/utils/template-loader'
import { saveAppleModel, saveAppleTemplate, saveGoogleData, saveVariables, saveMetaData, saveGoogleClass, saveGoogleObject, saveScannerConfiguration, uploadTemplatePreview } from '/providers/templates-api'
import Variables from '/utils/manage-variables'
import captureAppleFrontPreview from './apple/capture-front-preview'
import captureGoogleFrontPreview from './google/capture-front-preview'
import { normaliseEditableHighlightMode } from '/utils/editable-highlight-mode'

let skipGoogleClassSave = false
let skipGoogleObjectSave = false

const stripDataUrlPrefix = dataUrl => dataUrl.replace(/^data:image\/png;base64,/, "")

const Editor = () => {
  const { apiSource, apiKey, user, refreshTemplates, client, selectedClientId } = useContext(AppContext)

  const navigate = useNavigate()
  const { templateId } = useParams()

  const [templateLoaded, setTemplateLoaded] = useState(false)

  const [smartixPassType, setSmartixPassType] = useState(null)
  const [applePassType, setApplePassType] = useState(null)
  const [appleSubType, setAppleSubType] = useState(null)
  const [googlePassType, setGooglePassType] = useState(null)
  const [googleSubType, setGoogleSubType] = useState(null)

  const [appleModel, setAppleModel] = useState()
  const [appleTemplate, setAppleTemplate] = useState()
  const [googleBoth, setGoogleBoth] = useState()
  const [googleClass, setGoogleClass] = useState()
  const [googleObject, setGoogleObject] = useState()
  const [variables, setVariables] = useState({})
  const [scannerData, setScannerData] = useState({})
  const [isManagedPassTemplate, setisManagedPassTemplate] = useState(false)
  const [passGroupId, setPassGroupId] = useState(null)
  const [variableCatalog, setVariableCatalog] = useState([])
  const [templateClientId, setTemplateClientId] = useState(null)
  const [applePreviewVersion] = useState(Date.now())
  const [applePreviewDataUrl, setApplePreviewDataUrl] = useState(null)
  const [metaData, setMetaData] = useState({})
  const [classes, setClasses] = useState("")
  const [showVariables, setShowVariables] = useState(false)
  const [savingCount, setSavingCount] = useState(0)
  const [googlePreviewSaving, setGooglePreviewSaving] = useState(false)
  const [googleClassPushing, setGoogleClassPushing] = useState(false)

  const selectedClient = client?.available_clients?.find(item => item.client_id?.toString() === selectedClientId?.toString())
  const canInitiateGooglePreview = Number(templateClientId) === 0 || Number(selectedClient?.parent_client_id) === 0
  const savedState = useRef({})
  const skipSave = useRef({})
  const skipVariablesSave = useRef(false)
  const currentState = useRef({})
  const saveRevisions = useRef({})
  const applePreviewSave = useRef(null)

  currentState.current = { appleTemplate, variables, googleClass, googleObject, googleBoth }

  const beginSave = key => {
    const revision = (saveRevisions.current[key] ?? 0) + 1
    saveRevisions.current[key] = revision
    return revision
  }

  const isLatestSave = (key, revision) => saveRevisions.current[key] === revision

  const markSaveToSkip = key => {
    skipSave.current[key] = true
  }

  const skipSaveIfMarked = key => {
    if (!skipSave.current[key]) return false
    delete skipSave.current[key]
    return true
  }

  const updateGoogleBoth = next => {
    skipGoogleClassSave = true
    skipGoogleObjectSave = true
    setGoogleClass(_.cloneDeep(next.googleClass))
    setGoogleObject(_.cloneDeep(next.googleObject))
    setGoogleBoth(_.cloneDeep(next))
  }

  const runSave = async save => {
    // When enabled this shows a saving dialog. User has to wait...
//    setSavingCount(count => count + 1)

    try {
      await save()
    }
    catch (err) {
      console.error(err)
    }
    finally {
//      setSavingCount(count => Math.max(0, count - 1))
      refreshTemplates()
    }
  }

  const restoreIfUnchanged = (key, attemptedValue, setter, revision) => {
    if ((revision === undefined || isLatestSave(key, revision)) && _.isEqual(currentState.current[key], attemptedValue)) {
      markSaveToSkip(key)
      setter(_.cloneDeep(savedState.current[key]))
    }
  }

  const restoreGoogleBothIfUnchanged = (attemptedClass, attemptedObject, revision) => {
    const attempted = { googleClass: attemptedClass, googleObject: attemptedObject }
    const isUnchanged = _.isEqual(currentState.current.googleBoth, attempted)
      && _.isEqual(currentState.current.googleClass, attemptedClass)
      && _.isEqual(currentState.current.googleObject, attemptedObject)
    if (isLatestSave('googleBoth', revision) && isUnchanged) {
      markSaveToSkip('googleClass')
      markSaveToSkip('googleObject')
      setGoogleClass(_.cloneDeep(savedState.current.googleClass))
      setGoogleObject(_.cloneDeep(savedState.current.googleObject))
    }
  }

  const saveApplePreview = () => {
    if (applePreviewSave.current) return applePreviewSave.current

    const save = (async () => {
      console.log("saveApplePreview: gate", {
        templateId,
        templateClientId,
      })

      let dataUrl
      try {
        dataUrl = await captureAppleFrontPreview()
      }
      catch (err) {
        console.error('Apple preview capture failed', err)
        return { ok: false, error: { message: 'Apple preview capture failed; one or more images could not be loaded' } }
      }

      setApplePreviewDataUrl(dataUrl)

      const base64Data = stripDataUrlPrefix(dataUrl)

      const result = await uploadTemplatePreview({ apiSource, apiKey, templateId, base64Data, platform: "apple", ownerClientId: templateClientId })
      return result
    })()

    applePreviewSave.current = save
    save.then(
      () => {
        if (applePreviewSave.current === save) applePreviewSave.current = null
      },
      () => {
        if (applePreviewSave.current === save) applePreviewSave.current = null
      }
    )
    return save
  }

  const saveGooglePreview = async () => {
    console.log("saveGooglePreview: gate", {
      templateId,
      templateClientId,
    })

    let dataUrl
    try {
      dataUrl = await captureGoogleFrontPreview()
    }
    catch (err) {
      console.error('Google preview capture failed', err)
      return { ok: false, error: { message: 'Google preview capture failed; one or more images could not be loaded' } }
    }

    const base64Data = stripDataUrlPrefix(dataUrl)

    const result = await uploadTemplatePreview({ apiSource, apiKey, templateId, base64Data, platform: "google", ownerClientId: templateClientId })
    console.log("saveGooglePreview: upload result", { templateId, result })
    return result
  }

  const initiateGooglePreview = async () => {
    if (!canInitiateGooglePreview || googlePreviewSaving) return

    setGooglePreviewSaving(true)
    try {
      const [appleResult, googleResult] = await Promise.allSettled([
        saveApplePreview(),
        saveGooglePreview()
      ])
      const appleOk = appleResult.status === 'fulfilled' && appleResult.value?.ok
      const googleOk = googleResult.status === 'fulfilled' && googleResult.value?.ok
      const successfulPlatforms = [appleOk && 'Apple', googleOk && 'Google'].filter(Boolean)
      const failedPlatforms = [!appleOk && 'Apple', !googleOk && 'Google'].filter(Boolean)

      enqueueSnackbar({
        message: failedPlatforms.length === 0
          ? 'Apple and Google preview snapshots uploaded'
          : successfulPlatforms.length === 0
            ? 'Apple and Google preview uploads failed'
            : `${successfulPlatforms.join(' and ')} preview uploaded; ${failedPlatforms.join(' and ')} preview upload failed`,
        variant: failedPlatforms.length === 0 ? 'success' : 'error'
      })
    }
    catch (err) {
      console.error(err)
      enqueueSnackbar({ message: 'Apple and Google preview uploads failed', variant: 'error' })
    }
    finally {
      setGooglePreviewSaving(false)
    }
  }

  const pushGoogleClass = async () => {
    if (!canInitiateGooglePreview || googleClassPushing || !templateLoaded || !googleClass) return

    setGoogleClassPushing(true)
    try {
      const result = await saveGoogleClass({ apiSource, apiKey, templateId, googleClass: _.cloneDeep(googleClass) })
      enqueueSnackbar({
        message: result?.ok ? 'Google class pushed successfully' : 'Google class push failed',
        variant: result?.ok ? 'success' : 'error'
      })
    }
    catch (err) {
      console.error(err)
      enqueueSnackbar({ message: 'Google class push failed', variant: 'error' })
    }
    finally {
      setGoogleClassPushing(false)
    }
  }

  // Prevent hot refresh from causing data saves
  if (import.meta.hot) {
    import.meta.hot.on('vite:beforeUpdate', () => {
      setTemplateLoaded(false)
    })
    import.meta.hot.on('vite:afterUpdate', () => {
      setTemplateLoaded(true)
    })
  }

  useEffect(() => {
    if (templateId === null) navigate("/templates", { replace: true })

    loadInitialData()

  }, [])

  useEffect(() => {
    const mode = normaliseEditableHighlightMode(user.preferences.highlight_editable_items)
    const modeClass = mode === 'show' ? 'showEditFields' : mode === 'hover' ? 'hoverEditFields' : ''
    setClasses(`${modeClass} h-full w-full no-text-selection`)
  }, [user.preferences.highlight_editable_items])

  useEffect(() => {
    if (templateLoaded) {
      if (skipSaveIfMarked('appleModel')) return
      const save = async () => {
        const attemptedModel = _.cloneDeep(appleModel)
        let apiSaveSucceeded = false
        try {
//          enqueueSnackbar({ message: 'Saving Apple model', variant: 'light' })
          let result = await saveAppleModel({ apiSource, apiKey, templateId, appleModel: attemptedModel })

          if (!result.ok) throw result.error ?? new Error("Apple model save failed")
          apiSaveSucceeded = true
          savedState.current.appleModel = attemptedModel

          const previewResult = await saveApplePreview()
          if (!previewResult.ok) enqueueSnackbar({ message: previewResult.error?.message ?? 'Apple preview upload failed', variant: 'error' })

//          enqueueSnackbar({ message: 'Apple model saved', variant: 'success' })
//          refreshTemplates()
        }
        catch (err) {
          console.error(err)
          if (!apiSaveSucceeded) restoreIfUnchanged('appleModel', attemptedModel, setAppleModel)
          enqueueSnackbar({ message: 'Apple model save failed', variant: 'error' })
        }
      }
      runSave(save)
    }
  }, [appleModel])

  useEffect(() => {
    if (templateLoaded) {
      if (skipSaveIfMarked('appleTemplate')) return
      const save = async () => {
        const attemptedTemplate = _.cloneDeep(appleTemplate)
        const saveRevision = beginSave('appleTemplate')
        let apiSaveSucceeded = false
        try {
//          enqueueSnackbar({ message: 'Saving Apple template', variant: 'light' })

          let result = await saveAppleTemplate({ apiSource, apiKey, templateId, appleTemplate: attemptedTemplate })

          if (!result.ok) throw result.error ?? new Error("Apple template save failed")
          apiSaveSucceeded = true
          if (isLatestSave('appleTemplate', saveRevision) && _.isEqual(currentState.current.appleTemplate, attemptedTemplate)) {
            savedState.current.appleTemplate = attemptedTemplate
          }

          const previewResult = await saveApplePreview()
          if (!previewResult.ok) enqueueSnackbar({ message: previewResult.error?.message ?? 'Apple preview upload failed', variant: 'error' })

//          enqueueSnackbar({ message: 'Apple template saved', variant: 'success' })
//          refreshTemplates()
        }
        catch (err) {
          console.error(err)
          if (!apiSaveSucceeded) restoreIfUnchanged('appleTemplate', attemptedTemplate, setAppleTemplate, saveRevision)
          enqueueSnackbar({ message: 'Apple template save failed', variant: 'error' })
        }
      }
      runSave(save)
    }
  }, [appleTemplate])

  useEffect(() => {
    if (templateLoaded) {
      if (skipSaveIfMarked('googleClass')) return
      const wideLogoPending = !_.isEqual(
        _.get(variables, "images.google.wide_logo"),
        _.get(savedState.current.variables, "images.google.wide_logo")
      )
      if (wideLogoPending) return

      const save = async () => {
        const attemptedClass = _.cloneDeep(googleClass)
        const saveRevision = beginSave('googleClass')
//        enqueueSnackbar({ message: 'Saving Google class', variant: 'light' })

        let result = await saveGoogleClass({ apiSource, apiKey, templateId, googleClass: attemptedClass })

        if (result.ok) {
          const previewResult = await saveGooglePreview()
          if (!previewResult.ok) enqueueSnackbar({ message: previewResult.error?.message ?? 'Google preview upload failed', variant: 'error' })

//          enqueueSnackbar({ message: 'Google class saved', variant: 'success' })

          const savedClass = _.cloneDeep(result.classData)
          if (isLatestSave('googleClass', saveRevision) && _.isEqual(currentState.current.googleClass, attemptedClass)) {
            skipGoogleClassSave = true
            savedState.current.googleClass = savedClass
            setGoogleClass(savedClass)
          }

//          refreshTemplates()
        } else {
          restoreIfUnchanged('googleClass', attemptedClass, setGoogleClass, saveRevision)
          enqueueSnackbar({ message: 'Google class save failed', variant: 'error' })
        }
      }
      if (skipGoogleClassSave) skipGoogleClassSave = false
      else runSave(save)
    }
  }, [googleClass])

  useEffect(() => {
    if (templateLoaded) {
      if (skipSaveIfMarked('googleObject')) return
      const save = async () => {
        const attemptedObject = _.cloneDeep(googleObject)
        const saveRevision = beginSave('googleObject')
//        enqueueSnackbar({ message: 'Saving Google object', variant: 'light' })

        let result = await saveGoogleObject({ apiSource, apiKey, templateId, googleObject: attemptedObject })

        if (result.ok) {
          const previewResult = await saveGooglePreview()
          if (!previewResult.ok) enqueueSnackbar({ message: previewResult.error?.message ?? 'Google preview upload failed', variant: 'error' })

//          enqueueSnackbar({ message: 'Google object saved', variant: 'success' })

          const savedObject = _.cloneDeep(result.objectData)
          if (isLatestSave('googleObject', saveRevision) && _.isEqual(currentState.current.googleObject, attemptedObject)) {
            skipGoogleObjectSave = true
            savedState.current.googleObject = savedObject
            setGoogleObject(savedObject)
          }

//          refreshTemplates()
        } else {
          restoreIfUnchanged('googleObject', attemptedObject, setGoogleObject, saveRevision)
          enqueueSnackbar({ message: 'Google object save failed', variant: 'error' })
        }
      }
      if (skipGoogleObjectSave) skipGoogleObjectSave = false
      else runSave(save)
    }
  }, [googleObject])

  useEffect(() => {
    if (templateLoaded) {
      if (skipSaveIfMarked('googleBoth')) return
      const save = async () => {
//        enqueueSnackbar({ message: 'Saving Google class & object', variant: 'light' })

        let attemptedClass = _.cloneDeep(googleBoth.googleClass)
        let attemptedObject = _.cloneDeep(googleBoth.googleObject)
        const saveRevision = beginSave('googleBoth')

        let result = await saveGoogleData({ apiSource, apiKey, templateId, googleClass: attemptedClass, googleObject: attemptedObject })

        if (result.ok) {
          const previewResult = await saveGooglePreview()
          if (!previewResult.ok) enqueueSnackbar({ message: previewResult.error?.message ?? 'Google preview upload failed', variant: 'error' })

//          enqueueSnackbar({ message: 'Google class & object saved', variant: 'success' })

        // Ensure we do not fire the useEffects for googleClass and googleObject
          const savedClass = _.cloneDeep(result.classData)
          const savedObject = _.cloneDeep(result.objectData)
          const attempted = { googleClass: attemptedClass, googleObject: attemptedObject }
          const isUnchanged = _.isEqual(currentState.current.googleBoth, attempted)
            && _.isEqual(currentState.current.googleClass, attemptedClass)
            && _.isEqual(currentState.current.googleObject, attemptedObject)
          if (isLatestSave('googleBoth', saveRevision) && isUnchanged) {
            skipGoogleClassSave = true
            skipGoogleObjectSave = true
            savedState.current.googleClass = savedClass
            savedState.current.googleObject = savedObject
            setGoogleClass(savedClass)
            setGoogleObject(savedObject)
          }

//          refreshTemplates()
        } else {
          restoreGoogleBothIfUnchanged(attemptedClass, attemptedObject, saveRevision)
          enqueueSnackbar({ message: 'Google class & object save failed; local changes were reverted', variant: 'error' })
        }
      }
      runSave(save)
    }
  }, [googleBoth])

  useEffect(() => {
    if (templateLoaded) {
      if (skipSaveIfMarked('variables')) return
      if (skipVariablesSave.current) {
        skipVariablesSave.current = false
        return
      }

      const save = async () => {
        const attemptedVariables = _.cloneDeep(variables)
        const saveRevision = beginSave('variables')
        const variablesToSave = _.cloneDeep(attemptedVariables)
        const wideLogoChanged = !_.isEqual(
          _.get(attemptedVariables, "images.google.wide_logo"),
          _.get(savedState.current.variables, "images.google.wide_logo")
        )

        // Check if pass.validity values are valid date strings
        const startDate = _.get(variablesToSave, "pass.validity.start")
        if (startDate) {
          if (!isValidDateTimeInput(startDate)) _.set(variablesToSave,"pass.validity.start",null)
        }

        const endDate = _.get(variablesToSave, "pass.validity.end")
        if (endDate) {
          if (!isValidDateTimeInput(endDate)) _.set(variablesToSave,"pass.validity.end",null)
        }

//        enqueueSnackbar({ message: 'Saving dynamic values', variant: 'light' })

        const customVariables = variableCatalog
          .filter(item => item.var_name?.startsWith("custom."))
          .map(item => ({
            var_name: item.var_name,
            encrypted: Number(item.encrypted) === 1 ? 1 : 0,
            var_type: item.var_type ?? "text",
            allowable_in_pass_data: Number(item.allowable_in_pass_data) === 1 ? 1 : 0
          }))

        const variableOverrides = Object.fromEntries(variableCatalog
          .filter(item => !item.var_name?.startsWith("custom."))
          .filter(item => item.default_allowable_in_pass_data !== undefined)
          .filter(item => Number(item.allowable_in_pass_data) !== Number(item.default_allowable_in_pass_data))
          .map(item => [item.var_name, { allowable_in_pass_data: Number(item.allowable_in_pass_data) === 1 ? 1 : 0 }]))

        let result = await saveVariables({ apiSource, apiKey, templateId, variables: variablesToSave, customVariables, variableOverrides })

        if (result.ok) {
          const applePreviewResult = await saveApplePreview()
          if (!applePreviewResult.ok) enqueueSnackbar({ message: applePreviewResult.error?.message ?? 'Apple preview upload failed', variant: 'error' })

          const googlePreviewResult = await saveGooglePreview()
          if (!googlePreviewResult.ok) enqueueSnackbar({ message: googlePreviewResult.error?.message ?? 'Google preview upload failed', variant: 'error' })

          if (isLatestSave('variables', saveRevision) && _.isEqual(currentState.current.variables, attemptedVariables)) {
            savedState.current.variables = variablesToSave
          }
          if (isLatestSave('variables', saveRevision) && _.isEqual(currentState.current.variables, attemptedVariables) && !_.isEqual(variables, variablesToSave)) {
            skipVariablesSave.current = true
            setVariables(variablesToSave)
          }
          if (wideLogoChanged && isLatestSave('variables', saveRevision) && _.isEqual(currentState.current.variables, attemptedVariables)) {
            savedState.current.googleClass = _.cloneDeep(currentState.current.googleClass)
          }
//          enqueueSnackbar({ message: 'Dynamic values saved', variant: 'success' })
//          refreshTemplates()
        } else {
          if (wideLogoChanged && _.isEqual(currentState.current.variables, attemptedVariables)) {
            markSaveToSkip('googleClass')
            setGoogleClass(_.cloneDeep(savedState.current.googleClass))
          }
          restoreIfUnchanged('variables', attemptedVariables, setVariables, saveRevision)
          enqueueSnackbar({ message: 'Dynamic values save failed', variant: 'error' })
        }
      }
      runSave(save)
    }
  }, [variables])

  useEffect(() => {
    if (templateLoaded) {
      if (skipSaveIfMarked('scannerData')) return
      const save = async () => {
        const attemptedScannerData = _.cloneDeep(scannerData)
//        enqueueSnackbar({ message: 'Saving scanner data', variant: 'light' })

        let result = await saveScannerConfiguration({ apiSource, apiKey, templateId, scannerData: attemptedScannerData })

        if (result.ok) {
//          enqueueSnackbar({ message: 'Scanner data saved', variant: 'success' })
          savedState.current.scannerData = attemptedScannerData
//          refreshTemplates()
        } else {
          restoreIfUnchanged('scannerData', attemptedScannerData, setScannerData)
          enqueueSnackbar({ message: 'Scanner data save failed', variant: 'error' })
        }
      }
      runSave(save)
    }
  }, [scannerData])

  useEffect(() => {
    if (templateLoaded) {
      if (skipSaveIfMarked('metaData')) return
      const save = async () => {
        const attemptedMetaData = _.cloneDeep(metaData)
//        enqueueSnackbar({ message: 'Saving meta data', variant: 'light' })

        let result = await saveMetaData({ apiSource, apiKey, templateId, metaData: attemptedMetaData })

        if (result.ok) {
//          enqueueSnackbar({ message: 'Meta data saved', variant: 'success' })
          savedState.current.metaData = attemptedMetaData
//          refreshTemplates()
        } else {
          restoreIfUnchanged('metaData', attemptedMetaData, setMetaData)
          enqueueSnackbar({ message: 'Meta data save failed', variant: 'error' })
        }
      }
      runSave(save)
    }
  }, [metaData])

  async function loadInitialData() {

    const { metaData, isManagedPassTemplate, smartixPassType, appleModel, appleTemplate, googleObject, variables, scannerData, googleClass, applePassType, appleSubType, googlePassType, googleSubType, passGroupId, variableCatalog, templateClientId } = await loadTemplate({ apiSource, apiKey, templateId })

    setSmartixPassType(smartixPassType)
    setApplePassType(applePassType)
    setAppleSubType(appleSubType)
    setGooglePassType(googlePassType)
    setGoogleSubType(googleSubType)
    setisManagedPassTemplate(isManagedPassTemplate)
    setTemplateClientId(templateClientId)
    setPassGroupId(passGroupId)
    setVariableCatalog(variableCatalog)

    setAppleModel(appleModel)
    setAppleTemplate(appleTemplate)
    setGoogleClass(googleClass)
    setGoogleObject(googleObject)
    setVariables(variables)
    setMetaData(metaData)
    setScannerData(scannerData)

    savedState.current = {
      appleModel: _.cloneDeep(appleModel),
      appleTemplate: _.cloneDeep(appleTemplate),
      googleClass: _.cloneDeep(googleClass),
      googleObject: _.cloneDeep(googleObject),
      variables: _.cloneDeep(variables),
      scannerData: _.cloneDeep(scannerData),
      metaData: _.cloneDeep(metaData)
    }

    // Delay setting this to true until next render cycle to ensure above 
    // useEffects do not pick up our initial load as a change
    setTimeout(e => setTemplateLoaded(true), 100)
  }

  const openVariablesManager = () => setShowVariables(true)

  return (
    <>
      <div className={classes}>
        <Body
          templateId={templateId}
          smartixPassType={smartixPassType}
          variableCatalog={variableCatalog}
          appleTemplate={appleTemplate}
          googleObject={googleObject}
          variables={variables}
          setVariables={setVariables}
          scannerData={scannerData}
          setScannerData={setScannerData}
          appleModel={appleModel}
          googleClass={googleClass}
          applePassType={applePassType}
          appleSubType={appleSubType}
          googlePassType={googlePassType}
          templateLoaded={templateLoaded}
          googleSubType={googleSubType}
          setGoogleBoth={updateGoogleBoth}
          setAppleModel={setAppleModel}
          setAppleTemplate={setAppleTemplate}
          setGoogleClass={setGoogleClass}
          setGoogleObject={setGoogleObject}
          isManagedPassTemplate={isManagedPassTemplate}
          metaData={metaData}
          setMetaData={setMetaData}
          openVariablesManager={openVariablesManager}
          applePreviewVersion={applePreviewVersion}
          canInitiateGooglePreview={canInitiateGooglePreview}
          googlePreviewSaving={googlePreviewSaving}
          initiateGooglePreview={initiateGooglePreview}
          googleClassPushing={googleClassPushing}
          pushGoogleClass={pushGoogleClass}
          applePreviewDataUrl={applePreviewDataUrl}
        />
      </div>

      <Backdrop open={savingCount > 0} sx={{ color: '#fff', zIndex: 99999 }}>
        <CircularProgress className='mr-5' /> Saving changes...
      </Backdrop>

      <Variables
        open={showVariables}
        close={() => setShowVariables(false)}
        variables={variables}
        setVariables={setVariables}
        catalogItems={variableCatalog}
        setCatalogItems={setVariableCatalog}
        isManagedPassTemplate={isManagedPassTemplate}
        passGroupId={passGroupId}
        appleModel={appleModel}
        appleTemplate={appleTemplate}
        googleClass={googleClass}
        googleObject={googleObject}
      />
    </>
  )
}

export default Editor
